import { isNavGroup, type NavItem } from '@realtyfeed/app-shell';
import { navigation } from './navigation';

/**
 * Which endpoint permissions a **route** needs, derived from the nav tree.
 *
 * ### Why this exists
 *
 * Until the permission audit (5H.5) the only permission check in the app was the one that hides
 * a sidebar entry. Hiding a link does not stop anyone reaching the screen: `/staff` was one typed
 * URL away for any signed-in session, whatever its grant. CLAUDE.md has always said route-level
 * gating is **two** things — `PrivateRoute` for signed-in vs signed-out, and a permission check
 * for whether this session may see this screen, and that *both* apply. Only the first existed.
 *
 * ### One source of truth
 *
 * A route does not carry its own permission list. It inherits the one from the nav entry that
 * owns it, matched by **longest path prefix**: `/customer-list/detail/12` is owned by the
 * Customers entry (`/customer-list`), and `/mls-list/logs` by the MLS Logs entry rather than the
 * MLS Providers one, because its prefix is longer. That keeps `navigation.ts` the only place a
 * screen's permissions are written down, so a ported screen cannot be gated in the sidebar and
 * open at its URL.
 *
 * An entry's `matchPaths` counts as an owned prefix too — that field exists precisely for a
 * screen whose path does not sit under its section's, and the audit found three of them.
 *
 * ### What an empty list means
 *
 * No restriction. That is right for `/`, `/dashboard`, `/profile` and `/error`, which every
 * signed-in staff member may see, and it is what the three deliberately-unrestricted nav entries
 * already rely on (`UNRESTRICTED_NAV_IDS` in `navigation.ts`).
 */

type Owner = { path: string; apiPath: readonly string[] };

const collectOwners = (items: NavItem[]): Owner[] =>
  items.flatMap((item) => {
    if (isNavGroup(item)) return collectOwners(item.children);
    const paths = [item.to, ...(item.matchPaths ?? [])].filter(
      (path): path is string => typeof path === 'string' && path.length > 0
    );
    return paths.map((path) => ({ path, apiPath: item.apiPath ?? [] }));
  });

/** Every owned prefix, longest first, so the most specific entry wins. */
export const ROUTE_PERMISSION_OWNERS: Owner[] = collectOwners(navigation).sort(
  (a, b) => b.path.length - a.path.length
);

const owns = (ownerPath: string, pathname: string): boolean =>
  pathname === ownerPath || pathname.startsWith(`${ownerPath}/`);

/**
 * The endpoint paths a pathname requires — an empty list meaning "no restriction".
 *
 * Takes the concrete pathname rather than the route pattern, because every owner path is
 * concrete (a nav entry never carries a `:param`) and the prefix match is what relates the two.
 */
export const requiredPathsFor = (pathname: string): readonly string[] => {
  const owner = ROUTE_PERMISSION_OWNERS.find((candidate) => owns(candidate.path, pathname));
  return owner?.apiPath ?? [];
};
